import React, { useState } from 'react';
import { ReportStatusTypesEnum, SystemGeneratedDocumentsTypesEnum } from '../../../utilities/Constants';

import { CollabModalProvider } from '../collab-report/modals/CollabModalContext';
import CollabReportController from '../collab-report/CollabReportController';
import { ILookupModel } from '../../../interfaces/ILookup';
import { IReportParametersModel } from '../../../interfaces/Report/IReport';
import { IValidation } from '../../../interfaces/IError';
import { KpiReportCommentsModal } from './KpiReportCommentsModal';
import { KpiSettingsModal } from './KpiSettingsModal';
import { LocalRoutes } from '../../../utilities/LocalRoutes';
import { cloneDeep } from 'lodash';
import { useParams } from 'react-router-dom';
import { useZoneContext } from '../../../contexts/useZoneContext';

export interface IKpiReportInputModel extends IReportParametersModel {
    comments_AvgSettlementTrends?: string;
    comments_DataSourceFootnote?: string;
    comments_DefenseSpend?: string;
    comments_OpenActiveCases?: string;
    comments_SettlementSpend?: string;
    endDate?: string | undefined;
}

export interface IKpiReportModel {
    parameters?: IKpiReportInputModel;
    renderReport: boolean;
    refreshToken?: string;
}

const getDefaultReportInputModel = (reportGuid: string | undefined, zoneGuid: string) => {
    const inputModel: IKpiReportInputModel = {
        amount: undefined,
        endDate: undefined,
        entityGuid: reportGuid,
        reportType: { id: SystemGeneratedDocumentsTypesEnum.KpiReport.Value },
        reportTypeId: SystemGeneratedDocumentsTypesEnum.KpiReport.Value,
        organizationGuid: zoneGuid
    };

    return inputModel;
};

export function KpiReport() {
    const { guid: reportGuid } = useParams();
    const { zoneGuid } = useZoneContext();

    const [inputModel, setInputModel] = React.useState<IKpiReportInputModel>(
        getDefaultReportInputModel(reportGuid, zoneGuid)
    );

    const [data, setData] = useState<IKpiReportModel>({
        renderReport: !!reportGuid,
        parameters: {
            // amount: undefined,
            // endDate: undefined,
            // entityGuid: reportGuid,
            // reportType: { id: MyDocumentsTypesEnum.KpiReport.Value },
            // organizationGuid: zoneGuid
        } as IKpiReportInputModel,
        refreshToken: Math.random().toString(),
    });
    const [showSettingsModal, setShowSettingsModal] = React.useState<boolean>(false);
    const [commentsModal, setCommentsModal] = React.useState<{
        open: boolean;
        section?: number;
    }>({ open: false });
    const [reportStatusId, setReportStatusId] = React.useState<number | undefined>();

    const [validation, setValidation] = React.useState<IValidation>({});
    const [shouldRunReport, setShouldRunReport] = React.useState<boolean>(false);

    const handleInputModelChange = (newInputModel: any) => {
        setInputModel(newInputModel as IKpiReportInputModel);
    };

    const handleShowSettingsModal = (show: boolean) => {
        setShowSettingsModal(show);
    };

    const handleShowCommentsModal = (show: boolean, section: number) => {
        setCommentsModal({ open: show, section });
    };

    const handleSettingModalSubmit = (model: IKpiReportInputModel) => {
        setInputModel((prev) => ({ ...prev, ...model }));
        setShouldRunReport(true);
    };

    const handleReportStatusChange = (status: ILookupModel) => {
        setReportStatusId(status.id);
    };

    const handleCommentsDialogSaveClick = (
        updatedInputModel: IKpiReportInputModel,
        refreshOnClose: boolean
    ) => {
        if (refreshOnClose) {
            setInputModel((prev) => ({ ...prev, ...updatedInputModel }));
            setShouldRunReport(true);
        }
        setCommentsModal({ open: false });
    };

    React.useEffect(() => {
        if (!reportGuid) {
            setShowSettingsModal(true);
        }
    }, [reportGuid]);

    React.useEffect(() => {
        if (shouldRunReport) {
            const token = Math.random().toString();
            const inputModelCopy = cloneDeep(inputModel);

            setData((prev) => ({
                ...prev,
                refreshToken: token,
                parameters: inputModelCopy,
                renderReport: true,
            }));
            setShouldRunReport(false);
            setShowSettingsModal(false);
        }
    }, [shouldRunReport]);

    const exportParameters = {
        reportType: {
            id: SystemGeneratedDocumentsTypesEnum.KpiReport.Value,
        },
        date: {
            endDate: inputModel.endDate,
        },
        amount: inputModel.amount,
        reportSectionDetails: inputModel.reportSectionDetails,
        entityGuid: inputModel.entityGuid,
        saveReport: inputModel.saveReport,
        organizationGuid: inputModel.organizationGuid,
    };
    
    const isDraftStatus = reportStatusId === ReportStatusTypesEnum.Draft;

    return (
        <>
            <CollabModalProvider>
                <CollabReportController<IKpiReportModel, IKpiReportInputModel>
                    collabReportUrl={LocalRoutes.KpiReportCollab}
                    data={data}
                    exportSettings={{ parameters: exportParameters }}
                    inputModel={inputModel}
                    onInputModelChange={handleInputModelChange}
                    onReportStatusChange={handleReportStatusChange}
                    onShowCommentsModal={handleShowCommentsModal}
                    onShowSettingsModal={handleShowSettingsModal}
                    pageTitle="KPI Report"
                    reportTypeId={SystemGeneratedDocumentsTypesEnum.KpiReport.Value}
                    getReportData={async () =>
                        setData((prev) => ({ ...prev, refreshToken: Math.random().toString() }))
                    }
                />
            </CollabModalProvider>
            {showSettingsModal && (
                <KpiSettingsModal
                    disabled={!!reportGuid}
                    initialInputModel={inputModel}
                    onHide={() => setShowSettingsModal(false)}
                    onSubmit={handleSettingModalSubmit}
                    open={showSettingsModal}
                    readonly={false}
                    reportGuid={reportGuid}
                    setValidation={setValidation}
                    validation={validation}
                />
            )}
            <KpiReportCommentsModal
                onHide={() => setCommentsModal({ open: false })}
                open={commentsModal.open}
                onSubmit={handleCommentsDialogSaveClick}
                reportInputModel={inputModel}
                disabled={!!reportGuid && !isDraftStatus}
            />
        </>
    );
}
